import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TimeoutError } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { of } from 'rxjs/internal/observable/of';
import { catchError } from 'rxjs/internal/operators/catchError';
import { map } from 'rxjs/internal/operators/map';
import { timeout } from 'rxjs/operators';
import { IResponse } from '../../shared/models/app';
import { Environment } from '../../shared/models/environment';
import { TimeOutApi } from '../constants/api.constant';
import { ClientError } from '../error/model/client-error';
import { ServerError } from '../error/model/server-error';

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  /*********************Properties*********************/
  protected get options() {
    const headers = {};
    headers['Accept'] = 'application/json';
    headers['Access-Control-Allow-Methods'] = 'GET,PUT,POST,PATCH,DELETE';
    headers['Allow'] = 'GET,PUT,POST,PATCH,DELETE';
    headers['Cache-Control'] = 'no-cache, no-store';
    headers['Pragma'] = 'no-cache';
    headers['Expires'] = 'Sat, 01 Jan 2000 00:00:00 GMT';
    headers['Content-Type'] = 'application/json';
    return {
      headers,
    };
  }

  private readonly httpMethod = {
    GET: 'get',
    POST: 'post',
    PUT: 'put',
    DELETE: 'delete',
  };
  /*********************Properties*********************/

  /*********************Constructor*********************/
  constructor(protected http: HttpClient) {}
  /*********************Constructor*********************/

  /*********************Service Methods*********************/

  protected fetch<T>(url: string, options?: any | {}): Observable<T> {
    return this.invokeService<T>(this.httpMethod.GET, url, null, options);
  }

  protected post<T>(
    url: string,
    body: object,
    options?: any | {}
  ): Observable<T> {
    return this.invokeService<T>(this.httpMethod.POST, url, body, options);
  }

  protected put<T>(
    url: string,
    body: object,
    options?: any | {}
  ): Observable<T> {
    return this.invokeService<T>(this.httpMethod.PUT, url, body, options);
  }

  protected delete<T>(
    url: string,
    body: object,
    options?: any | {}
  ): Observable<T> {
    return this.invokeService<T>(this.httpMethod.DELETE, url, body, options);
  }

  /*********************Service Methods*********************/

  /*********************Private Methods*********************/

  public invokeService<T>(
    method: string,
    url: string,
    body?: object | null,
    options?: any | {}
  ): Observable<T> {
    let reqOptions = JSON.parse(JSON.stringify(this.options));
    reqOptions = Object.assign(reqOptions, options);
    if (options && options.headers) {
      const headers = Object.clone(this.options.headers);
      reqOptions.headers = Object.assign(headers, reqOptions.headers);
    }

    if (body) {
      reqOptions.body = body;
    }
    const apiTimeOut = Environment.getInstance().appConfig.apiTimeOut ?
      Environment.getInstance().appConfig.apiTimeOut * 1000 :
      TimeOutApi.DEFAULT_TIME_OUT;

    return this.http.request<T>(method, url, reqOptions).pipe(
      timeout(apiTimeOut),
      map((response) => this.onHttpResponse(response)),
      catchError(this.onHttpError())
    );
  }

  public onHttpResponse(response) {
    const res: IResponse = {
      data: response,
      error: null,
      hasError: false,
    };

    return res;
  }

  public onHttpError() {
    return (error: any): Observable<any> => {
      let err = error;
      if (error instanceof TimeoutError) {
        err = error.message;
      }
      if (error instanceof HttpErrorResponse) {
        if (error.error instanceof ErrorEvent) {
          // A client-side or network error occurred.
          err = new ClientError(error.error.message);
        } else {
          // A server-side error occurred.
          err = new ServerError(error.error, error.status);
        }

        err.status = error.status;
        err.statusText = error.statusText;
      } else {
        console.log(error);
      }

      const res: IResponse = {
        data: null,
        error: err,
        hasError: true,
      };

      return of(res);
    };
  }

  /*********************Private Methods*********************/
}
